import { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react'
import type { ThemeMode, GameThemeInput } from '@merels/games/types/theme'
import type { DerivedTheme } from '@merels/games/types/theme'
import type { PlayerPieceComponent } from '@merels/sdk'
import { resolveTheme } from '../deps/mode/theme/theme-resolver'

interface ThemeContextValue {
  userTheme: ThemeMode
  setUserTheme: (theme: ThemeMode) => void
  /** The theme actually being used (may differ if game doesn't support user preference) */
  effectiveTheme: ThemeMode
  gameThemeInput: GameThemeInput | null
  setGameThemeInput: (input: GameThemeInput | null) => void
  /** Optional piece glyph from the active game (null when absent / no game). */
  playerPiece: PlayerPieceComponent | null
  setPlayerPiece: (component: PlayerPieceComponent | null) => void
  /** Resolved derived theme for current effective theme */
  resolvedTheme: DerivedTheme
  /** Get player color for a specific player ID */
  getPlayerColor: (playerId: number) => { primary: string; background: string; foreground: string } | null
  /** Get all player colors with foreground (game or defaults) */
  getAllPlayerColorsWithForeground: () => Array<{ primary: string; background: string; foreground: string }>
  /** Get accent color (from core tokens) */
  getAccent: () => string
  /** Get app background color (from core tokens) */
  getAppBackground: () => string
  /** Get title font from game theme or null */
  getTitleFont: () => string | null
  /** Compatibility: Get app color shades (maps accent to main) */
  getAppColor: () => { main: string; background: string; muted: string; accent: string }
  /** Compatibility: Get all player colors without foreground */
  getAllPlayerColors: () => Array<{ primary: string; background: string }>
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export interface ThemeProviderProps {
  children: ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [userTheme, setUserThemeState] = useState<ThemeMode>(() => {
    // Load from sessionStorage, default to 'dark'
    if (typeof window === 'undefined') return 'dark'
    const stored = sessionStorage.getItem('userTheme')
    return (stored === 'light' || stored === 'dark') ? stored : 'dark'
  })
  
  const [gameThemeInput, setGameThemeInput] = useState<GameThemeInput | null>(null)
  // Store via updater wrapper — PlayerPiece is a function; React would call it as a state updater.
  const [playerPiece, setPlayerPieceState] = useState<PlayerPieceComponent | null>(null)
  // Keep the context action stable because game loading effects depend on it.
  const setPlayerPiece = useCallback((component: PlayerPieceComponent | null) => {
    setPlayerPieceState(() => component)
  }, [])

  const setUserTheme = (theme: ThemeMode) => {
    setUserThemeState(theme)
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('userTheme', theme)
    }
  }

  const effectiveTheme: ThemeMode = useMemo(() => {
    if (!gameThemeInput || !gameThemeInput.supportedThemes) return userTheme
    if (gameThemeInput.supportedThemes.includes(userTheme)) {
      return userTheme
    }
    // Fallback to first supported theme
    return gameThemeInput.supportedThemes[0] || 'dark'
  }, [gameThemeInput, userTheme])

  // Resolve derived theme for current effective theme
  const resolvedTheme = useMemo(() => {
    const theme = resolveTheme(gameThemeInput, effectiveTheme)
    console.log('[ThemeContext] Resolved theme:', {
      hasGame: !!gameThemeInput,
      effectiveTheme,
      core: theme.core,
      surfaceBackground: theme.core.surfaceBackground,
      appBackground: theme.core.appBackground,
    })
    return theme
  }, [gameThemeInput, effectiveTheme])

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement
    const wasDark = root.classList.contains('dark')
    const shouldBeDark = effectiveTheme === 'dark'
    
    if (shouldBeDark !== wasDark) {
      console.log(`[ThemeContext] Switching theme: ${wasDark ? 'dark' : 'light'} -> ${effectiveTheme}`, {
        userTheme,
        gameSupportedThemes: gameThemeInput?.supportedThemes,
        effectiveTheme
      })
    }
    
    if (shouldBeDark) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
  }, [effectiveTheme, userTheme, gameThemeInput])

  const getPlayerColor = useMemo(() => {
    return (playerId: number) => {
      if (resolvedTheme.playerColors[playerId]) {
        return resolvedTheme.playerColors[playerId]
      }
      return null
    }
  }, [resolvedTheme])
  
  /** Get all player colors (game or defaults) */
  const getAllPlayerColorsWithForeground = useMemo(() => {
    return () => {
      return resolvedTheme.playerColors
    }
  }, [resolvedTheme])

  /** Compatibility: Get all player colors without foreground */
  const getAllPlayerColors = useMemo(() => {
    return () => {
      return resolvedTheme.playerColors.map(p => ({ primary: p.primary, background: p.background }))
    }
  }, [resolvedTheme])

  /** Get accent color */
  const getAccent = useMemo(() => {
    return () => resolvedTheme.core.accent
  }, [resolvedTheme])

  /** Get app background color */
  const getAppBackground = useMemo(() => {
    return () => resolvedTheme.core.appBackground
  }, [resolvedTheme])

  /** Get title font from game theme or null */
  const getTitleFont = useMemo(() => {
    return () => gameThemeInput?.titleFont || null
  }, [gameThemeInput])

  /** Compatibility: Get app color shades (maps accent to main) */
  const getAppColor = useMemo(() => {
    return () => {
      return {
        main: resolvedTheme.core.accent,
        background: resolvedTheme.core.appBackground,
        muted: resolvedTheme.derived.mutedBackground,
        accent: resolvedTheme.core.accent,
      }
    }
  }, [resolvedTheme])

  return (
    <ThemeContext.Provider value={{
      userTheme,
      setUserTheme,
      effectiveTheme,
      gameThemeInput,
      setGameThemeInput,
      playerPiece,
      setPlayerPiece,
      resolvedTheme,
      getPlayerColor,
      getAllPlayerColorsWithForeground,
      getAccent,
      getAppBackground,
      getTitleFont,
      getAppColor,
      getAllPlayerColors,
    }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return context
}
